import React from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Avatar,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { colors } from "../../../../theme/colors";
import {
  CandidateReportsWrapper,
  StyledTableCell,
  StyledCircularProgress,
} from "./style";
import { useCandidateReports } from "./useCandidateReports";

const CandidateReports = () => {
  const {
    tableDataRef,
    downloading,
    interviews,
    isLoading,
    isError,
    error,
    isLastPage,
    filterName,
    filterOptions,
    handleNextPage,
    handlePrevPage,
    handleFilterChange,
    handleRowsPerPageChange,
    handleInterviewTypeChange,
    handleDownloadReport,
    handleResetFilters,
  } = useCandidateReports();

  const activeFilterCount =
    (filterName ? 1 : 0) +
    (filterOptions.interviewType !== "All" ? 1 : 0);

  const SkeletonRow = () => (
    <TableRow>
      <StyledTableCell>
        <Box className="candidates-primary-details">
          <Skeleton variant="circular" width={40} height={40} />
          <Box className="candidate-details-box">
            <Skeleton variant="text" width={120} height={30} />
            <Skeleton variant="text" width={180} height={20} />
          </Box>
        </Box>
      </StyledTableCell>
      <StyledTableCell>
        <Skeleton variant="text" width={100} height={30} />
      </StyledTableCell>
      <StyledTableCell>
        <Skeleton variant="text" width={120} height={30} />
      </StyledTableCell>
      <StyledTableCell>
        <Skeleton variant="text" width={100} height={30} />
      </StyledTableCell>
      <StyledTableCell>
        <Skeleton variant="text" width={60} height={30} />
      </StyledTableCell>
      <TableCell className="download-cell">
        <Skeleton
          variant="rectangular"
          sx={{ borderRadius: "3px" }}
          width={120}
          height={35}
        />
      </TableCell>
    </TableRow>
  );

  const renderTableBody = () => {
    if (isLoading) {
      return Array.from({ length: filterOptions.limit }).map((_, index) => (
        <SkeletonRow key={`skeleton-${index}`} />
      ));
    }

    if (interviews.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <NoAccountsIcon sx={{ color: "#919EAB" }} />
              <Typography sx={{ color: "#919EAB", fontSize: "16px" }}>
                Candidate Not Found
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    return interviews.map((interview) => (
      <TableRow key={interview?._id}>
        <StyledTableCell>
          <Box className="candidates-primary-details">
            <Avatar
              src={interview.profilePic || ""}
              alt={interview.candidateName || "profile"}
            />
            <Box className="candidate-details-box">
              <Typography>{interview.candidateName || ""}</Typography>
              <Typography>{interview.candidateEmail || ""}</Typography>
            </Box>
          </Box>
        </StyledTableCell>
        <StyledTableCell>{interview.applyFor || ""}</StyledTableCell>
        <StyledTableCell>{interview.candidatePhone || ""}</StyledTableCell>
        <StyledTableCell>
          <Chip
            label={
              interview.interviewType === "AI Bot +"
                ? "AI Bot +"
                : interview.interviewType === "Coding Test"
                ? "Coding Assessment"
                : "AI Interview"
            }
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              backgroundColor:
                interview.interviewType === "AI Bot +"
                  ? "#f3e5f5"
                  : interview.interviewType === "Coding Test"
                  ? "#e8f5e9"
                  : colors.primaryLight,
              color:
                interview.interviewType === "AI Bot +"
                  ? "#7b1fa2"
                  : interview.interviewType === "Coding Test"
                  ? "#2e7d32"
                  : colors.primary,
            }}
          />
        </StyledTableCell>
        <StyledTableCell>
          {interview.interviewType === "AI Bot +" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "11px", color: "#919EAB", minWidth: 44 }}>Coding</Typography>
                <Box className="score-badge">
                  {interview.codingScore != null ? `${interview.codingScore}%` : "N/A"}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "11px", color: "#919EAB", minWidth: 44 }}>AI</Typography>
                <Box className="score-badge">
                  {interview.aiScore != null ? `${interview.aiScore}%` : "N/A"}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box className="score-badge">
              {interview.maxScore != null ? `${interview.maxScore}%` : "N/A"}
            </Box>
          )}
        </StyledTableCell>
        <TableCell className="download-cell">
          {downloading[interview._id] ? (
            <Tooltip title="Report generation is in progress. Please wait..." arrow>
              <Button className="download_button" disabled>
                Generating report... <StyledCircularProgress size={18} />
              </Button>
            </Tooltip>
          ) : (
            <Button
              className="download_button"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() =>
                handleDownloadReport(
                  interview._id,
                  interview.candidateName,
                  interview.interviewType,
                  interview.aiInterviewId,
                )
              }
            >
              Download
            </Button>
          )}
        </TableCell>
      </TableRow>
    ));
  };

  if (isError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
        <NoAccountsIcon sx={{ fontSize: 64, color: "#d1d5db" }} />
        <Typography variant="h6" fontWeight={600} color="text.secondary">
          Error loading candidates
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error?.message || "Unknown error occurred"}
        </Typography>
      </Box>
    );
  }

  return (
    <CandidateReportsWrapper>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            pb: 2,
            mb: 2,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  letterSpacing: "-0.5px",
                  fontSize: "1.35rem",
                }}
              >
                Candidate Reports
              </Typography>
              <Chip
                label={`${interviews.length} Result`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: colors.primaryLight,
                  color: colors.primary,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              View, filter, and download candidate assessment and interview performance reports.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: { xs: "100%", md: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search candidate name or email..."
              value={filterName}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                  </InputAdornment>
                ),
                endAdornment: filterName ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange({ target: { value: "" } })}
                      edge="end"
                    >
                      <ClearIcon sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                minWidth: { xs: "100%", sm: "280px" },
                flex: { sm: 1, md: "0 1 340px" },
                "& .MuiOutlinedInput-root": {
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  backgroundColor: filterName ? colors.primaryLight : "#ffffff",
                  "& fieldset": {
                    borderColor: filterName ? colors.primaryLightBorder : "#cbd5e1",
                  },
                  "&:hover fieldset": {
                    borderColor: colors.primary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary,
                    borderWidth: "1.5px",
                  },
                },
              }}
            />

            <FormControl size="small" sx={{ minWidth: "190px" }}>
              <Select
                value={filterOptions.interviewType}
                onChange={handleInterviewTypeChange}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <SmartToyOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  backgroundColor: filterOptions.interviewType !== "All" ? colors.primaryLight : "#ffffff",
                  borderColor: filterOptions.interviewType !== "All" ? colors.primaryLightBorder : "#cbd5e1",
                  color: filterOptions.interviewType !== "All" ? colors.primary : "#334155",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value="All">All Interview Types</MenuItem>
                <MenuItem value="AI Bot">AI Interview</MenuItem>
                <MenuItem value="Coding Test">Coding Assessment</MenuItem>
                <MenuItem value="AI Bot +">AI Bot +</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: "140px" }}>
              <Select
                value={filterOptions.limit}
                onChange={handleRowsPerPageChange}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <TableRowsOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  backgroundColor: "#ffffff",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value={5}>5 per page</MenuItem>
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={20}>20 per page</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={handleResetFilters}
              startIcon={<RotateLeftIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary,
                backgroundColor: colors.primaryLight,
                border: `1px solid ${colors.primaryLightBorder}`,
                borderRadius: "8px",
                px: 1.5,
                py: "6px",
                height: "38px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: colors.sidebarSelected || colors.primaryLight,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Clear {activeFilterCount} {activeFilterCount === 1 ? "Filter" : "Filters"}
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper} className="custom-scrollbar">
        <Table stickyHeader ref={tableDataRef}>
          <TableHead>
            <TableRow>
              {[
                "Candidate Name",
                "Position",
                "Phone Number",
                "Interview Type",
                "Score",
                "Download Report",
              ].map((header) => (
                <TableCell key={header} className="table-header-cell">
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "20px",
        }}
      >
        <Button
          className="btn"
          variant="contained"
          onClick={handlePrevPage}
          disabled={filterOptions.page <= 1 || isLoading}
          startIcon={<NavigateBeforeIcon />}
        >
          Previous
        </Button>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Page {filterOptions.page}
        </Typography>
        <Button
          className="btn"
          variant="contained"
          onClick={handleNextPage}
          disabled={isLastPage || interviews.length === 0 || isLoading}
          endIcon={<NavigateNextIcon />}
        >
          Next
        </Button>
      </Box>
    </CandidateReportsWrapper>
  );
};

export default CandidateReports;
